import { Box, Grid } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { CustomizedDataGrid } from 'components/customized_data_grid';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import RowTypography from 'components/Table/RowTypography';
import CustomBarChartWidget from 'components/Widgets/CustomBarChart';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { fetchHappySignalByTechnology } from 'service/service';
import CaptureExportControls from 'utils/CaptureExportControls';

function HappySignalByTechnology() {
    const theme = useTheme();
    const [isLoading, setIsLoading] = React.useState(false);
    const [happinessIndexData, setHappinessIndexData] = useState<any>(null);
    const [, setHappinessIndexBarChartOptions] = useState<any>({});
    const [lostTimeData, setLostTimeData] = useState<any>(null);
    const [, setLostTimeBarChartOptions] = useState<any>({});
    const [sortingInfo] = useState({ sortBy: 'size', sortDir: 'desc' });
    const [showBottom10Technology, setShowBottom10Technology] = useState(false);
    const [bottom10ByHappinessIndexData, setBottom10ByHappinessIndexData] = useState<any>(null);
    const [showBottom10LostTime, setShowBottom10LostTime] = useState(false);
    const [bottom10ByLostTimeData, setBottom10ByLostTimeData] = useState<any>(null);
    const [isExporting, setIsExporting] = useState(false);

    const [happinessLastYearAvg, setHappinessLastYearAvg] = useState<any>(null);
    const [lostTimeLastYearAvg, setLostTimeLastYearAvg] = useState<any>(null);

    const isXOps = true;
    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };
    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());
    const initialMonth = getInitialMonth(); // <--- 1. ADDED THIS LINE

    const defaultColumnNames = ['Technology', 'Happiness Index', 'Lost Time (Hours)', 'Responses'];

    const handleSorting = (newSortingInfo: any) => {
    };

    const handlepageSizeChange = (event: any) => {
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    };

    const getColumns = () => {
        const columnMap: Array<{ key: string; label: string }> = [
            { key: 'technology', label: 'Technology' },
            { key: 'happinessIndex', label: 'Happiness Index' },
            { key: 'lostTime', label: 'Lost Time (Hours)' },
            { key: 'responses', label: 'Responses' }
        ];

        return columnMap.map((col) => {
            let label = col.label;
            return {
                key: col.key,
                name: label,
                resizable: true,
                // width: '230px',
                formatter: ({ row }: any) => {
                    let value = row[col.key];
                    return <RowTypography>{value}</RowTypography>;
                }
            };
        });
    };

    const getSimpleBarChartOptions = (
        data: { name: string; value: number }[],
        label: string,
        barColor: string = theme.palette.secondary.main,
        lastYearAvg: any // Keep lastYearAvg parameter even if not used, to match signature
    ) => {
        // Ensure data is an array before mapping
        const validData = Array.isArray(data) ? data : [];
        const values = validData.map((d) => d.value);

        // Handle cases where values array might be empty
        const maxVal = values.length > 0 ? Math.max(...values) : 0;
        const minVal = values.length > 0 ? Math.min(...values) : 0;


        const yMax = Math.ceil(maxVal / 10) * 10 || 10; // Ensure yMax is at least 10
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            title: { show: false },
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const tech = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Technology:</strong> ${tech}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
            xAxis: {
                type: 'category',
                data: validData.map((item) => item.name), // Use validData here
                axisLabel: {
                    color: `${theme.palette.text.subtle}`,
                    rotate: window.innerWidth < 600 ? 30 : 0
                }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                splitLine: { show: false },
                axisLabel: { color: `${theme.palette.text.subtle}` }
            },
            series: [
                {
                    name: label,
                    type: 'bar',
                    data: validData.map((item) => item.value), // Use validData here
                    barWidth: '30%',
                    barMaxWidth: '30%',
                    itemStyle: {
                        color: barColor
                    },
                    label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' }
                }
                // Removed the '2024 Average' line series as lastYearAvg might not be available/needed here
            ],
            legend: {
                orient: 'horizontal',
                bottom: '1%',
                left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10,
                itemHeight: 10,
                icon: 'roundRect'
            }
        };
    };


    useEffect(() => {
        fetchAndPopulateData(filterMonth);
    }, []);

    const [tablesByCategory, setTablesByCategory] = useState<Record<string, any[]>>({});

    const fetchAndPopulateData = async (filterMonth: string) => {
        try {
            setIsLoading(true);
            const response = await fetchHappySignalByTechnology(filterMonth);

            if (response.success && response.data) {
                const technologies = response.data.technologies;
                const top10ByHappinessIndex = response.data.top10ByHappinessIndex;
                const bottom10ByHappinessIndex = response.data.bottom10ByHappinessIndex;
                const top10ByLostTime = response.data.top10ByLostTime;
                const bottom10ByLostTime = response.data.bottom10ByLostTime;
                const lastYearAverages = response.data.lastYearAverages; // Keep this line

                // Normalize bar chart data
                const top10HappinessChartData = top10ByHappinessIndex.map((item: any) => ({
                    name: item.name,
                    value: item.happinessIndex
                }));

                const bottom10ByHappinessChartData = bottom10ByHappinessIndex.map((item: any) => ({
                    name: item.name,
                    value: item.happinessIndex
                }));

                const top10LostTimeChartData = top10ByLostTime.map((item: any) => ({
                    name: item.name,
                    value: item.lostTimeHours
                }));

                const bottom10LostTimeChartData = bottom10ByLostTime.map((item: any) => ({
                    name: item.name,
                    value: item.lostTimeHours
                }));

                // --- You might need these averages later if you re-add the line to the chart ---
                const currentHappinessLastYearAvg = lastYearAverages?.happinessIndex; // Safely access
                const currentLostTimeLastYearAvg = lastYearAverages?.lostTimeHours; // Safely access
                setHappinessLastYearAvg(currentHappinessLastYearAvg); // Store if needed
                setLostTimeLastYearAvg(currentLostTimeLastYearAvg);   // Store if needed


                // --- Build table data dynamically ---
                const categoryMap: Record<string, any[]> = {};
                technologies.forEach((item: any) => {
                    const category = item.category || "Others";
                    if (!categoryMap[category]) categoryMap[category] = [];
                    categoryMap[category].push({
                        technology: item.name,
                        happinessIndex: item.happinessIndex,
                        lostTime: item.lostTimeHours,
                        responses: item.responses
                    });
                });

                // Set states
                setHappinessIndexData(top10HappinessChartData);
                setBottom10ByHappinessIndexData(bottom10ByHappinessChartData);
                setLostTimeData(top10LostTimeChartData);
                setBottom10ByLostTimeData(bottom10LostTimeChartData);

                // Pass the potentially null/undefined average value; getSimpleBarChartOptions handles it
                setHappinessIndexBarChartOptions(getSimpleBarChartOptions(top10HappinessChartData, 'Happiness Index', '#FF00FF', currentHappinessLastYearAvg));
                setLostTimeBarChartOptions(getSimpleBarChartOptions(top10LostTimeChartData, 'Lost Time (Hours)', `${theme.palette.boxColor.orange.color}`, currentLostTimeLastYearAvg));


                setTablesByCategory(categoryMap);
            }
        } catch (err) {
            console.error('Error fetching technology data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDateChange = (selectedDate: string) => {
        fetchAndPopulateData(selectedDate);
    };

    // <--- 2. ADDED THIS FUNCTION --->
    const generateTitleSuffix = () => {
        if (filterMonth !== initialMonth) {
            const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
            const formattedMonth = isYearFilter ? filterMonth : moment(filterMonth, 'YYYY-MM').format('MMMM YYYY');
            return ` - ${formattedMonth}`;
        }
        return ''; // Return empty string if it's the default month
    };

    // <--- 3. ADDED THIS LINE --->
    const dynamicSuffix = generateTitleSuffix();


    const handleExportToExcel = () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const storedPref = localStorage.getItem('happy_signal_by_technology_list_user_col_pref');
            const storedColumnNames: string[] | null = storedPref ? JSON.parse(storedPref) : null;
            const preferredColumnNames: string[] = storedColumnNames?.length
                ? storedColumnNames
                : defaultColumnNames;

            const allColumns = getColumns();
            const nameToKey: Record<string, string> = {};
            allColumns.forEach((col: any) => { nameToKey[col.name] = col.key; });

            const exportData = Object.entries(tablesByCategory).map((row: any) => {
                const exportRow: Record<string, any> = {};
                preferredColumnNames.forEach((colName: string) => {
                    const key = nameToKey[colName];
                    if (key !== undefined) {
                        const val = row[key];
                        exportRow[colName] = val ?? '';
                    }
                });
                return exportRow;
            });

            const escapeCsv = (v: any) => `"${String(v ?? '').replaceAll('"', '""')}"`;
            const header = preferredColumnNames.filter(n => nameToKey[n] !== undefined).join(',');
            const rows = exportData.map((r: any) =>
                preferredColumnNames
                    .filter((n: string) => nameToKey[n] !== undefined)
                    .map((n: string) => escapeCsv(r[n]))
                    .join(',')
            );
            const csv = [header, ...rows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `HappySignal_ByTechnology_${filterMonth}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            {/* Move DateFilterDropdown to right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <YearMonthPicker
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                />
                {/* <DateFilterDropdown
                    customMonth={customMonth}
                    filterMonth={filterMonth}
                    setCustomMonth={setCustomMonth}
                    setFilterMonth={setFilterMonth}
                    handleChange={handleDateChange}
                    isXOps={true}
                    boxProps={{ sx: dropDownSx }}
                /> */}
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <CustomBarChartWidget
                        title={`By Happiness Index${dynamicSuffix}`} // <--- 4. UPDATED THIS LINE
                        isLoading={isLoading}
                        data={showBottom10Technology ? bottom10ByHappinessIndexData || [] : happinessIndexData || []}
                        options={getSimpleBarChartOptions(
                            showBottom10Technology ? bottom10ByHappinessIndexData || [] : happinessIndexData || [],
                            'Happiness Index',
                            '#FF00FF', happinessLastYearAvg)} // Pass avg here
                        showBottom10={showBottom10Technology}
                        setShowBottom10={setShowBottom10Technology}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'end' }}>
                        <CustomBarChartWidget
                            title={`By Lost Time(Hours)${dynamicSuffix}`}
                            isLoading={isLoading}
                            data={showBottom10LostTime ? bottom10ByLostTimeData || [] : lostTimeData || []}
                            options={getSimpleBarChartOptions(
                                showBottom10LostTime ? bottom10ByLostTimeData || [] : lostTimeData || [],
                                'Lost Time (Hours)', theme.palette.secondary.main, lostTimeLastYearAvg)}
                            showBottom10={showBottom10LostTime}
                            setShowBottom10={setShowBottom10LostTime}
                            sx={{flexGrow: '1'}}
                        />
                        <CaptureExportControls
                            onScreenshot={() => {}}
                            screenCapturing={false}
                            onExport={handleExportToExcel}
                            isExporting={isExporting}
                            isVisible={true}
                            showExport={true}
                            type="snow"
                            exportLabel="Export to CSV"
                            sx={{ ml: 0, '& #exclude-camera': { display: 'none' } }}
                        />
                    </Box>
                </Grid>
                {Object.entries(tablesByCategory).map(([category, data]) => (
                    <Grid item xs={12} key={category}>
                        <CustomizedDataGrid
                            title={category} // This title is the category name, not the chart title
                            tableType={`${category}_list`}
                            tableColumns={getColumns()}
                            tableData={data}
                            defaultColumnNames={defaultColumnNames}
                            onSorting={handleSorting}
                            currSorting={sortingInfo}
                            listLength={data.length}
                            handlepageSizeChange={handlepageSizeChange}
                            handlePageChange={handlePageChange}
                            isDateRangeFilter={false}
                            isDisabled={false}
                            isFetching={isLoading}
                            childRowClass={true}
                            isShowMoreButton={true}
                            isSortIcon={true}
                            showExport={false}
                            isRenderPagination={false}
                            isLighterTheme={true}
                        />
                    </Grid>
                ))}
                {/* <Grid item xs={12}>
                    <CustomizedDataGrid
                        title="Platforms"
                        tableType="Platforms_list"
                        tableColumns={getColumns()}
                        tableData={platformstableDataList}
                        defaultColumnNames={defaultColumnNames}
                        onSorting={handleSorting}
                        currSorting={sortingInfo}
                        // page={page}
                        // pageSize={pageSize}
                        // paginationData={paginationData}
                        listLength={platformstableDataList?.length}
                        handlepageSizeChange={handlepageSizeChange}
                        handlePageChange={handlePageChange}
                        // showGoToPage={true}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isFetching={isLoading}
                        // autoSetDraggedColumns={false}
                        childRowClass={true}
                        isShowMoreButton={true}
                        isSortIcon={true}
                        showExport={false}
                        isRenderPagination={false}
                    />
                </Grid>
                <Grid item xs={12}>
                    <CustomizedDataGrid
                        title="Others"
                        tableType="Others_list"
                        tableColumns={getColumns()}
                        tableData={otherstableDataList}
                        defaultColumnNames={defaultColumnNames}
                        onSorting={handleSorting}
                        currSorting={sortingInfo}
                        // page={page}
                        // pageSize={pageSize}
                        // paginationData={paginationData}
                        listLength={otherstableDataList?.length}
                        handlepageSizeChange={handlepageSizeChange}
                        handlePageChange={handlePageChange}
                        // showGoToPage={true}
                        isDateRangeFilter={false}
                        isDisabled={false}
                        isFetching={isLoading}
                        // autoSetDraggedColumns={false}
                        childRowClass={true}
                        isShowMoreButton={true}
                        isSortIcon={true}
                        showExport={false}
                        isRenderPagination={false}
                    />
                </Grid> */}
            </Grid>
        </Box>
    );
}

export default HappySignalByTechnology;